import AsyncData from '@sysarb/sysarb-api/models/common/AsyncData';
import type MappingWork from '@sysarb/sysarb-api/models/entities/MappingWork';
import type MappingFactor from '@sysarb/sysarb-api/models/entities/MappingFactor';
import type MappingFactorValue from '@sysarb/sysarb-api/models/entities/MappingFactorValue';
import type MappingWorkValuation from '@sysarb/sysarb-api/models/entities/MappingWorkValuation';
import type Mapping from '@sysarb/sysarb-api/models/entities/Mapping';
import { Guid } from 'guid-typescript';
import { ConditionOperator, LogicalOperator, OrderByDirection } from '@sysarb/sysarb-api/models/common/selection/Selection';
import type { EventService } from '@/services/EventService';
import { useApi, getApiManager } from '@/common/ApiHelper';
import type MappingEmployment from '@sysarb/sysarb-api/models/entities/MappingEmployment';
import type MappingWorkConnectedWorksComment from '@sysarb/sysarb-api/models/entities/MappingWorkConnectedWorksComment';
import type MappingWorkConnectedWorksTag from '@sysarb/sysarb-api/models/entities/MappingWorkConnectedWorksTag';
import type MappingWorkEqualWorksTag from '@sysarb/sysarb-api/models/entities/MappingWorkEqualWorksTag';
import type MappingEmploymentAction from '@sysarb/sysarb-api/models/entities/MappingEmploymentAction';
import type MappingEmploymentComment from '@sysarb/sysarb-api/models/entities/MappingEmploymentComment';
import type MappingEmploymentTag from '@sysarb/sysarb-api/models/entities/MappingEmploymentTag';
import { CurrencyHelper } from '@/common/CurrencyHelper';
import { NotificationStates } from '@/models/enums/NotificationStates';
import type { IPropertyUpdatedEvent } from '@sysarb/sysarb-api/models/common/event';
import debounce from 'debounce';
import { Gender } from '@sysarb/sysarb-types//general/enums/gender';
import { useNotificationStore } from '@/store/notificationStore';
import { useSystemStore } from '@/store/systemStore';
import type { IEntityDataStatePayload } from '@/store/payAuditStore';
import { usePayAuditStore } from '@/store/payAuditStore';
import { i18n } from '@/i18n';
import { Common } from '@/common/Common';
import { newApi } from '@/newApi';
import { getApiHost } from '@/appSettings';
import { RestManager } from '@/newApi/services/rest/RestManager';
import { storeToRefs } from 'pinia';
import { useLoginStore } from '@/login/stores/useLoginStore';
import { useLabsStore } from '@/views/jobArchitecture/composables/useLabs';
import type { ActualVsPredictedEmployment } from '@sysarb/sysarb-types-v2/services/v1/pay-equity-audit';
import type { Api } from '@sysarb/sysarb-api';
import { useRole } from '@/composables/useRole';

export class MappingContainer {
    /* MappingID to use when fetching the mapping employments */
    private static mappingId: Guid;

    /* ComponentId of parent component */
    private static componentId: Guid;

    /* Instance of the event service from the parent component */
    private static eventService: EventService;

    public static mappingHasNonDeclaredGender: boolean = false;

    private static readonly debounceMassiveChangeNotification: Function = debounce(MappingContainer.massiveChangeNotification, 500);

    private static readonly debounceChangeWorkNotification: Function = debounce(MappingContainer.changeWorkNotification, 500);

    private static currentRoleJunctionId: Guid;

    private static userId: Guid;

    private static createIndex(entityData: AsyncData<any>): AsyncData<Map<string, number>> {
        const entityIndex: AsyncData<Map<string, number>> = new AsyncData();
        void entityData.promise.then(elements => {
            const index: Map<string, number> = new Map<string, number>();
            if (elements != null) {
                elements.forEach((item: any, indexOf: number) => index.set(item.id!.toString(), indexOf));
            }
            entityIndex.setData(index);
        });
        return entityIndex;
    }

    /** Contents of a whole mapping */
    public static createMapping(mappingId: Guid, eventService: EventService | undefined, componentId: Guid, currentRoleJunctionId: Guid, userId: Guid): void {
        if (mappingId == null || mappingId.isEmpty() || eventService == null) {
            return;
        }
        // Add globl variables
        MappingContainer.mappingId = mappingId;
        MappingContainer.eventService = eventService;
        MappingContainer.componentId = componentId;
        MappingContainer.currentRoleJunctionId = currentRoleJunctionId;
        MappingContainer.userId = userId;

        // Set all parameters to their representive property
        MappingContainer.loadMapping();
        void MappingContainer.loadMappingEmployments();
        MappingContainer.loadMappingEmploymentActions();
        MappingContainer.loadMappingEmploymentComment();
        MappingContainer.loadMappingEmploymentTag();
        MappingContainer.loadMappingWorks();
        MappingContainer.loadMappingWorkConnectedWorksComment();
        MappingContainer.loadMappingWorkConnectedWorksTag();
        MappingContainer.loadMappingWorkEqualWorksTag();
        MappingContainer.loadMappingFactors();
        MappingContainer.loadMappingFactorValues();
        MappingContainer.loadMappingWorkValuationValues();
    }

    /**
     * Fetchs the mapping entity for the given ID
     */
    private static loadMapping(): void {
        const mapping = new AsyncData<Mapping>();
        const api = useApi();
        mapping.setData(api.mapping.getByID(MappingContainer.mappingId));
        // add the values to the store
        usePayAuditStore().setEntityStoreData('_mapping', { value: mapping });
    }

    /**
    * Fetches the mapping employments for the given ID
    * @param id MappingID to use when fetching the mapping employments.
    * @param eventService instance of the event service from the parent component
    * @param componentId componentId of parent component
    */
    public static async loadMappingEmployments(): Promise<void> {
        const { allowNonDeclaredGenderAnalysis } = useSystemStore();
        const payAuditStore = usePayAuditStore();
        const labsStore = useLabsStore();
        const { activeFlags } = storeToRefs(labsStore);
        const { role, instanceAccountType } = useLoginStore();
        const { getRoleDetails } = useRole();
        const { instanceTopic } = storeToRefs(useLoginStore());
        const api = useApi();

        payAuditStore.setEntityStoreData('_mappingEmployment', { value: new AsyncData<MappingEmployment[]>([]) });

        const { date, id } = await payAuditStore.mapping.promise;

        const mappingEmploymentsIndex: AsyncData<Map<string, number>> = MappingContainer.createIndex(payAuditStore.mappingEmployments);

        payAuditStore.setMappingEmploymentIndexStoreData(mappingEmploymentsIndex);

        const mappingEmploymentsList: any[] = [];

        const employments: MappingEmployment[] = await new RestManager(`https://${getApiHost()}/pay-equity-audit-service`).get(`v1/${instanceTopic.value}/pay-equity-audit/${MappingContainer.mappingId.toString()}/legacy-employments`);
        const predictedSalaries: Map<string, ActualVsPredictedEmployment> = new Map();

        if (id != null && activeFlags.value.includes('CostToFix')) {
            const result = await newApi.PayEquityAuditService.getActualVsPredictedSalaries(id.toString(), null);
            if (result != null) {
                // create a map of the predicted salaries wit hthe id on the predicted salary
                for (const item of result) {
                    predictedSalaries.set(item.id.toLowerCase(), item);
                }
            }
        }

        const currentRoleDetails = getRoleDetails(role?.role, instanceAccountType);
        const employmentCurrencies: Set<string> = new Set();
        const childNodes: string[] = [];
        if (currentRoleDetails?.bypassOrganisation !== true && MappingContainer.currentRoleJunctionId != null) {
            const nodes = await api.organisationNode.getChildren(MappingContainer.currentRoleJunctionId);
            childNodes.push(...nodes.map(x => x.id!.toString()));
        }
        const defaultCurrency = CurrencyHelper.getDefaultCurrency();
        let hasCompaRatio = true;

        // Update usesPerformance store state if any performance is something other than 0
        useSystemStore().setUsesPerformance(employments.some((x: MappingEmployment) => x.performance !== 0));

        // set for seen employments to avoid duplicates
        const seenEmployees: Set<string> = new Set();
        const duplicateEmployees: Set<string> = new Set();
        employments.forEach((employment: any) => {
            if (!allowNonDeclaredGenderAnalysis && employment.gender_Value === Gender.NOT_DECLARED) {
                MappingContainer.mappingHasNonDeclaredGender = true;
                return;
            }
            const employeeCurrency = employment.currency !== '' ? employment.currency : defaultCurrency;
            const predictedSalary = predictedSalaries.get(employment.id.toLowerCase());
            employmentCurrencies.add(employeeCurrency);
            hasCompaRatio = employment.compaRatio !== 0;
            // Map employments to an optimized object similar to the MappingEmployment entity class.
            // Containing only the values we need for the mapping view.
            const orgNodes: string[] = [];
            const availableToUser = currentRoleDetails?.bypassOrganisation === true
                ? true
                : childNodes.includes(employment.organisationNode);

            // loop the properties and add them to the array if set
            for (let level = 1; level <= 6; level++) {
                if (employment[`OrganisationNode${level}Name`] != null) orgNodes.push(employment[`OrganisationNode${level}Name`]);
            }

            // check if the employee has already been added to the list once
            let isDuplicate = false;
            if (seenEmployees.has(employment.socialSecurityNumber)) {
                isDuplicate = true;
                duplicateEmployees.add(employment.socialSecurityNumber);
            }

            const mappingEmployment = {
                // Set the accessability for lists based on organisation nodes
                availableToUser: Object.freeze(availableToUser),
                id: Object.freeze(Guid.parse(employment.id)),
                analysisComplete: employment.analysisComplete,
                analysisReasonCode: employment.analysisReasonCode,
                automatedAnalysisReasonCode: [],
                automatedAnalysisProblemFactors: null,
                bonusActualBonus: employment.bonusActualBonus,
                bonusBenefitsInKind: employment.bonusBenefitsInKind,
                bonusOneTimeBonus: employment.bonusOneTimeBonus,
                compaRatio: employment.compaRatio,
                companyCarAllowance: employment.companyCarAllowance,
                contract: employment.contract,
                costCenter: employment.costCenter != null
                    ? Object.freeze({ id: Guid.parse(employment.costCenter), name: employment.costCenterID_name, $type: 'CostCenterAvatar' })
                    : null,
                currency: employeeCurrency,
                dateOfBirth: employment.dateOfBirth != null ? Object.freeze(new Date(employment.dateOfBirth)) : null,
                employeeID: Object.freeze({ id: Guid.parse(employment.employeeID_ID), name: employment.employeeID_name, $type: 'EmployeeAvatar' }),
                employmentDate: employment.employmentDate != null ? new Date(employment.employmentDate) : null,
                employmentNumber: Object.freeze(employment.employmentNumber),
                employmentRate: employment.employmentRate,
                ethnicity: Object.freeze(employment.ethnicity),
                equity: employment.equity,
                formOfEmployment: employment.formOfEmployment,
                gender: Object.freeze({ value: employment.gender_Value }),
                // The MappingEmployment entity spells this field without the s
                holidayPaidHoliday: employment.holidaysPaidHoliday,
                lastPayIncrease: employment.lastPayIncrease,
                legalEntity: employment.legalEntity != null
                    ? Object.freeze({ id: Guid.parse(employment.legalEntity), name: employment.legalEntityID_name, $type: 'LegalEntityAvatar' })
                    : null,
                jobFamily: employment.jobFamily,
                locality: employment.locality,
                managerCode: employment.managerCode,
                mappingID: Object.freeze({ id: Guid.parse(employment.mappingID_ID), name: employment.mappingID_name, $type: 'MappingAvatar' }),
                mappingWorkID: employment.mappingWorkID != null
                    ? { id: Object.freeze(Guid.parse(employment.mappingWorkID)), name: employment.mappingWorkID_name, $type: Object.freeze('MappingWorkAvatar') }
                    : null,
                name: availableToUser ? Object.freeze(employment.name) : '',
                organisationNode: employment.organisationNode != null
                    ? { id: Object.freeze(Guid.parse(employment.organisationNode)), name: Object.freeze(orgNodes[orgNodes.length - 1]), $type: Object.freeze('OrganisationNodeAvatar') }
                    : null,
                organizationalLevel1: Object.freeze(orgNodes.length > 0 ? orgNodes[0] : undefined),
                organizationalLevel2: Object.freeze(orgNodes.length > 1 ? orgNodes[1] : undefined),
                organizationalLevel3: Object.freeze(orgNodes.length > 2 ? orgNodes[2] : undefined),
                organizationalLevel4: Object.freeze(orgNodes.length > 3 ? orgNodes[3] : undefined),
                organizationalLevel5: Object.freeze(orgNodes.length > 4 ? orgNodes[4] : undefined),
                organizationalLevel6: Object.freeze(orgNodes.length > 5 ? orgNodes[5] : undefined),
                overtime: employment.overtime,
                pensionOccupationalPension: employment.pensionOccupationalPension,
                performance: employment.performance,
                position: employment.position,
                positionLevel: employment.positionLevel,
                // Default simulated salaries to the original salaries
                simulatedSalary: employment.salary,
                simulatedTotalSalary: employment.totalSalary,
                individualSalaryIncrease: 0,
                groupSalaryIncrease: 0,
                salary: employment.salary,
                salaryExchange: employment.salaryExchange,
                salaryIncrement: employment.salaryIncrement,
                salaryIncrementSecond: employment.salaryIncrementSecond,
                salaryIncrementThird: employment.salaryIncrementThird,
                salaryDate: employment.salaryDate == null ? null : new Date(employment.salaryDate),
                salaryYear: employment.salaryYear === 0 ? null : employment.salaryYear,
                socialSecurityNumber: employment.socialSecurityNumber,
                statisticalCodeID: employment.statisticalCodeID != null
                    ? {
                        id: Object.freeze(Guid.parse(employment.statisticalCodeID)),
                        name: Object.freeze(employment.statisticalCodeID_name !== '' ? employment.statisticalCodeID_name : employment.statisticalCodeID_code),
                        $type: Object.freeze('StatisticalCodeAvatar')
                    }
                    : null,
                timeInCurrentPosition: employment.timeInCurrentPosition != null ? new Date(employment.timeInCurrentPosition) : null,
                totalSalary: employment.totalSalary,
                isDuplicate,
                age: employment.dateOfBirth != null ? Object.freeze(Common.calculateAge(employment.dateOfBirth, date)) : null,
                predictedSalaryOrganisation: predictedSalary != null ? predictedSalary.predictedSalary : null,
                additionalDataOne: employment.AdditionalDataOne,
                additionalDataSecond: employment.AdditionalDataSecond,
                additionalDataThird: employment.AdditionalDataThird,
                // Null when not set; stored as '' like the other string fields so the checkbox filters treat it as no value
                statutoryGrade: employment.statutoryGrade ?? '',
                occupationalClassification: employment.occupationalClassification ?? '',
            };

            mappingEmploymentsList.push(mappingEmployment);

            // add the unique employee id to the seen list
            seenEmployees.add(employment.socialSecurityNumber);
        });

        // update the unflagged employees
        if (duplicateEmployees.size > 0) {
            mappingEmploymentsList.forEach(employment => {
                if (duplicateEmployees.has(employment.socialSecurityNumber) && employment.isDuplicate !== true) {
                    employment.isDuplicate = true;
                }
            });
        }

        await CurrencyHelper.initGlobalCurrency(employmentCurrencies, hasCompaRatio);
        payAuditStore.mappingEmployments.setData(mappingEmploymentsList);

        // create the subscription
        MappingContainer.addSubscriptionsWithIndex(
            'MappingEmployment',
            payAuditStore.mappingEmployments,
            mappingEmploymentsIndex,
            'mappingID',
            MappingContainer.mappingId,
            async () => MappingContainer.loadMappingEmployments(),
            new Map<string, string>([
                ['mappingWorkName', 'mappingWorkID']
            ]),
        );

        // custom for updating dynamic organisation node values
        MappingContainer.eventService.subscribe({
            componentId: MappingContainer.componentId,
            entity: 'MappingEmployment',
            propertyUpdatedCallback: event => {
                if (event.propertyName === 'mappingWorkID') {
                    const employment = payAuditStore.mappingEmployments.data.find(x => x.id?.isEqualTo(event.entityID) === true);

                    if (employment == null) return;
                    employment.mappingWorkID = event.propertyValue;
                }

                if (event.propertyName === 'organisationNode') {
                    const organisationNodes = api.organisationNode.getOrganisationPath(event.propertyValue.id);

                    const employment = payAuditStore.mappingEmployments.data.find(x => x.id?.isEqualTo(event.entityID) === true);
                    if (employment != null) {
                        void organisationNodes.then(result => {
                            const orgNodes: string[] = [];

                            // loop the properties and add them to the array if set
                            for (let level = 6; level >= 1; level--) {
                                if (result[0][`OrganisationNode${level}Name`] != null) orgNodes.push(result[0][`OrganisationNode${level}Name`]);
                            }

                            // loop and set the properties that need to be updated
                            for (let index = 0; index < 6; index++) {
                                (employment as any)[`organizationalLevel${index + 1}`] = Object.freeze(orgNodes.length > index ? orgNodes[index] : null);
                            }
                        });
                    }
                }
            },
            bulkPropertyUpdatedCallback: events => {
                // create groups of events for currentuser and others.
                const eventGroups: Map<boolean, IPropertyUpdatedEvent[]> = new Map();
                events.forEach(event => {
                    if (event.propertyName === 'MappingWorkID') {
                        const userIsCurrentUser = MappingContainer.userId.toString().toLowerCase() === event.byUserID.toString().toLowerCase();
                        if (eventGroups.has(userIsCurrentUser)) {
                            eventGroups.get(userIsCurrentUser)!.push(event);
                        } else {
                            eventGroups.set(userIsCurrentUser, [event]);
                        }
                    }
                });

                // Add a notification depending on how many notifications there is in each group.
                eventGroups.forEach((group, userIsCurrentUser) => {
                    if (group.length === 1) {
                        const event = group[0];
                        const employment = payAuditStore.mappingEmployments.data.find(x => x.id?.toString().toLowerCase() === event.entityID.toString().toLowerCase());
                        if (employment != null) {
                            MappingContainer.debounceChangeWorkNotification(i18n.global.t('components.mappingContainer.employeeChangedWorkNotification', { name: employment.name, work: event.propertyValue.name }), userIsCurrentUser);
                        }
                    } else if (group.length > 1) {
                        MappingContainer.debounceChangeWorkNotification(i18n.global.t('components.mappingContainer.severalEmplpoymentsHasChangedWorkNotification'), userIsCurrentUser);
                    }
                });
            }
        });
    }

    /**
    * Fetches the mapping employment actions for the given ID
    */
    public static loadMappingEmploymentActions(): void {
        const mappingEmploymentActions = new AsyncData<MappingEmploymentAction[]>([]);
        usePayAuditStore().setEntityStoreData('_mappingEmploymentAction', { value: mappingEmploymentActions });
        const api = useApi();

        mappingEmploymentActions.setData(api.mappingEmploymentAction.getBySelection({
            Condition: {
                ConditionOperator: ConditionOperator.EqualTo,
                PropertyName: 'Mapping_ID',
                Value: MappingContainer.mappingId
            },
            QueriedFields: [
                'Completed',
                'PercentCostYear1',
                'PercentCostYear2',
                'PercentCostYear3',
                'MappingEmployment',
                'NewSalary'
            ]
        }));

        // custom subscriptions because adding values depends on the existance of a corresponding MappingEmployment
        MappingContainer.eventService.subscribe({
            componentId: MappingContainer.componentId,
            entity: 'MappingEmploymentAction',
            addedCallback: event => {
                const payAuditStore = usePayAuditStore();
                payAuditStore.addMappingEmploymentAction(event.entity as MappingEmploymentAction);
            },
            propertyUpdatedCallback: event => {
                // check we should be updating this entity
                const entityToUpdate = mappingEmploymentActions.data.find(x => x.id?.isEqualTo(event.entityID) === true);
                if (entityToUpdate != null) (entityToUpdate as any)[event.propertyName] = event.propertyValue;
            },
            deletedCallback: event => {
                const entityToDelete = mappingEmploymentActions.data.find(x => x.id?.isEqualTo(event.entityID) === true);
                if (entityToDelete != null) mappingEmploymentActions.data.splice(mappingEmploymentActions.data.indexOf(entityToDelete), 1);
            },
            bulkMassiveChangeCallback: () => {
                MappingContainer.debounceMassiveChangeNotification();
            },
            massiveChangeCallback: () => {
                MappingContainer.loadMappingEmploymentActions();
            }
        });
    }

    /**
    * Fetches the mapping employment comment for the given ID
    */
    public static loadMappingEmploymentComment(): void {
        const mappingEmploymentComments = new AsyncData<MappingEmploymentComment[]>([]);
        usePayAuditStore().setEntityStoreData('_mappingEmploymentComment', { value: mappingEmploymentComments });
        const api = useApi();

        mappingEmploymentComments.setData(api.mappingEmploymentComment.getBySelection({
            Condition: {
                ConditionOperator: ConditionOperator.EqualTo,
                PropertyName: 'MappingID_ID',
                Value: MappingContainer.mappingId
            },
            QueriedFields: [
                'MappingEmploymentID',
                'Comment'
            ]
        }));

        // custom subscriptions because adding values depends on the existance of a corresponding MappingEmployment
        MappingContainer.eventService.subscribe({
            componentId: MappingContainer.componentId,
            entity: 'MappingEmploymentComment',
            addedCallback: event => {
                const payAuditStore = usePayAuditStore();
                payAuditStore.addMappingEmploymentComment(event.entity as MappingEmploymentComment);
            },
            propertyUpdatedCallback: event => {
                // check we should be updating this entity
                const entityToUpdate = mappingEmploymentComments.data.find(x => x.id?.isEqualTo(event.entityID) === true);
                if (entityToUpdate != null) (entityToUpdate as any)[event.propertyName] = event.propertyValue;
            },
            deletedCallback: event => {
                const entityToDelete = mappingEmploymentComments.data.find(x => x.id?.isEqualTo(event.entityID) === true);
                if (entityToDelete != null) mappingEmploymentComments.data.splice(mappingEmploymentComments.data.indexOf(entityToDelete), 1);
            },
            bulkMassiveChangeCallback: () => {
                MappingContainer.debounceMassiveChangeNotification();
            },
            massiveChangeCallback: () => {
                MappingContainer.loadMappingEmploymentComment();
            }
        });
    }

    /**
    * Fetches the mapping employment tag for the given ID
    */
    public static loadMappingEmploymentTag(): void {
        const mappingEmploymentTags = new AsyncData<MappingEmploymentTag[]>([]);
        usePayAuditStore().setEntityStoreData('_mappingEmploymentTag', { value: mappingEmploymentTags });
        const api = useApi();

        mappingEmploymentTags.setData(api.mappingEmploymentTag.getBySelection({
            Condition: {
                LogicalOperator: LogicalOperator.And,
                Conditions: [
                    {
                        ConditionOperator: ConditionOperator.EqualTo,
                        PropertyName: 'MappingID_ID',
                        Value: MappingContainer.mappingId
                    },
                    {
                        ConditionOperator: ConditionOperator.NotEqualTo,
                        PropertyName: 'TagID_ID',
                        Value: null
                    },
                ]
            },
            QueriedFields: [
                'MappingEmploymentID',
                'TagID',
                'TagState'
            ]
        }));

        // custom subscriptions because adding values depends on the existance of a corresponding MappingEmployment
        MappingContainer.eventService.subscribe({
            componentId: MappingContainer.componentId,
            entity: 'MappingEmploymentTag',
            addedCallback: event => {
                const payAuditStore = usePayAuditStore();
                payAuditStore.addMappingEmploymentTag(event.entity as MappingEmploymentTag);
            },
            propertyUpdatedCallback: event => {
                // check we should be updating this entity
                const entityToUpdate = mappingEmploymentTags.data.find(x => x.id?.isEqualTo(event.entityID) === true);
                if (entityToUpdate != null) (entityToUpdate as any)[event.propertyName] = event.propertyValue;
            },
            deletedCallback: event => {
                const entityToDelete = mappingEmploymentTags.data.find(x => x.id?.isEqualTo(event.entityID) === true);
                if (entityToDelete != null) mappingEmploymentTags.data.splice(mappingEmploymentTags.data.indexOf(entityToDelete), 1);
            },
            bulkMassiveChangeCallback: () => {
                MappingContainer.debounceMassiveChangeNotification();
            },
            massiveChangeCallback: () => {
                MappingContainer.loadMappingEmploymentTag();
            }
        });
    }

    /**
    * Fetches the mapping works for the given ID
    */
    public static loadMappingWorks(): void {
        const mappingWorks = new AsyncData<MappingWork[]>([]);
        const api = useApi();
        usePayAuditStore().setEntityStoreData('_mappingWork', { value: mappingWorks });

        mappingWorks.setData(api.mappingWork.getBySelection({
            Condition: {
                ConditionOperator: ConditionOperator.EqualTo,
                PropertyName: 'MappingID_ID',
                Value: MappingContainer.mappingId
            },
            QueriedFields: [
                'AdditionalComments', // Only used in editor
                'AdditionalCommentsGenderEquality', // Only used in editor
                'BenchmarkLower',
                'BenchmarkMiddle',
                'BenchmarkUpper',
                'Comment',
                'CommentGenderEquality',
                'CostYear1',
                'CostYear1GenderEquality',
                'CostYear2',
                'CostYear2GenderEquality',
                'CostYear3',
                'CostYear3GenderEquality',
                'Description',
                'Measure',
                'MeasureGenderEquality',
                'Name',
                'AlwaysFemaleDominated',
                'TotalValuationPoints',
                'ValueWorkCompleted',
                'WorkComplete',
                'WorkNo'
            ]
        }));

        // create the subscription
        MappingContainer.addSubscriptions('MappingWork', mappingWorks, 'mappingID', MappingContainer.mappingId, () => MappingContainer.loadMappingWorks());
    }

    /**
    * Fetches the mapping factors for the given ID
    */
    public static loadMappingFactors(): void {
        const {mappingFactors} = usePayAuditStore();
        const api = useApi();

        mappingFactors.setData(api.mappingFactor.getBySelection({
            Condition: {
                ConditionOperator: ConditionOperator.EqualTo,
                PropertyName: 'MappingID_ID',
                Value: MappingContainer.mappingId
            },
            QueriedFields: [
                'Description',
                // 'MappingID',
                'Name',
                // 'OperationalAdministrator',
                // 'Status',
                'ValuationHelp',
                'WeightPercent',
            ]
        }));
        // create the subscription
        MappingContainer.addSubscriptions('MappingFactor', mappingFactors, 'mappingID', MappingContainer.mappingId, () => MappingContainer.loadMappingFactors());
    }

    /**
    * Fetches the mapping factor values for the given ID
    */
    public static loadMappingFactorValues(): void {
        // MappingFactorValues are only linked by MappingFactor to a Mapping

        const payAuditStore = usePayAuditStore();
        const api = useApi();
        void payAuditStore.mappingFactors.promise.then((mappingFactors: MappingFactor[]) => {
            // when the factors have resolved we can fetch factor values and set the subscriptions
            const factorIDs = mappingFactors.map(x => x.id!);
            const mappingFactorValues = new AsyncData<MappingFactorValue[]>([]);
            usePayAuditStore().setEntityStoreData('_mappingFactorValue', { value: mappingFactorValues });

            mappingFactorValues.setData(api.mappingFactorValue.getBySelection({
                Condition: {
                    PropertyName: 'mappingFactorID_ID',
                    ConditionOperator: ConditionOperator.In,
                    Value: factorIDs
                },
                OrderBy: [{
                    FieldName: 'MappingFactorIDSortIndex',
                    SortOrder: OrderByDirection.Asc
                }],
                QueriedFields: [
                    'Comment',
                    'MappingFactorID',
                    'MappingFactorIDSortIndex',
                    // 'Status',
                    'Value'
                ]
            }));

            // custom subscriptions because adding values depends on the existance of a corresponding MappingFactor
            MappingContainer.eventService.subscribe({
                componentId: MappingContainer.componentId,
                entity: 'MappingFactorValue',
                addedCallback: event => {
                    payAuditStore.addMappingFactorValue(event.entity as MappingFactorValue);
                },
                propertyUpdatedCallback: event => {
                    // check we should be updating this entity
                    const entityToUpdate = mappingFactorValues.data.find(x => x.id?.isEqualTo(event.entityID) === true);
                    if (entityToUpdate != null) (entityToUpdate as any)[event.propertyName] = event.propertyValue;
                },
                deletedCallback: event => {
                    const entityToDelete = mappingFactorValues.data.find(x => x.id?.isEqualTo(event.entityID) === true);
                    if (entityToDelete != null) mappingFactorValues.data.splice(mappingFactorValues.data.indexOf(entityToDelete), 1);
                },
                bulkMassiveChangeCallback: () => {
                    MappingContainer.debounceMassiveChangeNotification();
                },
                massiveChangeCallback: () => {
                    MappingContainer.loadMappingFactorValues();
                }
            });
        });
    }

    /**
    * Fetches the mapping work valuations values for the given ID
    */
    public static loadMappingWorkValuationValues(): void {
        const mappingWorkValuations = new AsyncData<MappingWorkValuation[]>([]);
        const api = useApi();
        usePayAuditStore().setEntityStoreData('_mappingWorkValuation', { value: mappingWorkValuations });

        mappingWorkValuations.setData(api.mappingWorkValuation.getBySelection({
            Condition: {
                ConditionOperator: ConditionOperator.EqualTo,
                PropertyName: 'MappingID_ID',
                Value: MappingContainer.mappingId
            },
            QueriedFields: [
                'MappingFactorID',
                'MappingFactorName',
                'MappingFactorValueComment',
                'MappingFactorValueID',
                // 'MappingFactorValueIDHelpText',
                // 'MappingID',
                'MappingWorkID',
                'MappingWorkName',
                // 'OperationalAdministrator',
                'Points',
                // 'Status',
                'ValuationNote'
            ]
        }));

        // create the subscription
        MappingContainer.addSubscriptions('MappingWorkValuation', mappingWorkValuations, 'mappingID', MappingContainer.mappingId, () => MappingContainer.loadMappingWorkValuationValues());
    }

    /**
    * Fetches the mapping work connected works comment for the given ID
    */
    public static loadMappingWorkConnectedWorksComment(): void {
        const mappingWorkConnectedWorksComments = new AsyncData<MappingWorkConnectedWorksComment[]>([]);
        const api = useApi();
        usePayAuditStore().setEntityStoreData('_mappingWorkConnectedWorksComment', { value: mappingWorkConnectedWorksComments });

        mappingWorkConnectedWorksComments.setData(api.mappingWorkConnectedWorksComment.getBySelection({
            Condition: {
                ConditionOperator: ConditionOperator.EqualTo,
                PropertyName: 'MappingID_ID',
                Value: MappingContainer.mappingId
            },
            QueriedFields: [
                // 'MappingID',
                'FemaleDominatedWorkID',
                'NonFemaleDominatedWorkID',
                'AnalyseText',
                'Comment'
            ]
        }));

        // custom subscriptions because adding values depends on the existance of a corresponding MappingWork
        MappingContainer.eventService.subscribe({
            componentId: MappingContainer.componentId,
            entity: 'MappingWorkConnectedWorksComment',
            addedCallback: event => {
                const payAuditStore = usePayAuditStore();
                payAuditStore.addMappingWorkConnectedWorksComment(event.entity as MappingWorkConnectedWorksComment);
            },
            propertyUpdatedCallback: event => {
                // check we should be updating this entity
                const entityToUpdate = mappingWorkConnectedWorksComments.data.find(x => x.id?.isEqualTo(event.entityID) === true);
                if (entityToUpdate != null) (entityToUpdate as any)[event.propertyName] = event.propertyValue;
            },
            deletedCallback: event => {
                const entityToDelete = mappingWorkConnectedWorksComments.data.find(x => x.id?.isEqualTo(event.entityID) === true);
                if (entityToDelete != null) mappingWorkConnectedWorksComments.data.splice(mappingWorkConnectedWorksComments.data.indexOf(entityToDelete), 1);
            },
            bulkMassiveChangeCallback: () => {
                MappingContainer.debounceMassiveChangeNotification();
            },
            massiveChangeCallback: () => {
                MappingContainer.loadMappingWorkConnectedWorksComment();
            }
        });
    }

    /**
    * Fetches the mapping work connected works tag for the given ID
    */
    public static loadMappingWorkConnectedWorksTag(): void {
        const mappingWorkConnectedWorksTags = new AsyncData<MappingWorkConnectedWorksTag[]>([]);
        const api = useApi();
        usePayAuditStore().setEntityStoreData('_mappingWorkConnectedWorksTag', { value: mappingWorkConnectedWorksTags });

        mappingWorkConnectedWorksTags.setData(api.mappingWorkConnectedWorksTag.getBySelection({
            Condition: {
                LogicalOperator: LogicalOperator.And,
                Conditions: [
                    {
                        ConditionOperator: ConditionOperator.EqualTo,
                        PropertyName: 'MappingID_ID',
                        Value: MappingContainer.mappingId
                    },
                    {
                        ConditionOperator: ConditionOperator.NotEqualTo,
                        PropertyName: 'TagID_ID',
                        Value: null
                    },
                ]
            },
            QueriedFields: [
                // 'MappingID',
                'FemaleDominatedWorkID',
                'NonFemaleDominatedWorkID',
                'TagID',
                'TagState'
            ]
        }));

        // custom subscriptions because adding values depends on the existance of a corresponding MappingWork
        MappingContainer.eventService.subscribe({
            componentId: MappingContainer.componentId,
            entity: 'MappingWorkConnectedWorksTag',
            addedCallback: event => {
                const payAuditStore = usePayAuditStore();
                payAuditStore.addMappingWorkConnectedWorksTag(event.entity as MappingWorkConnectedWorksTag);
            },
            propertyUpdatedCallback: event => {
                // check we should be updating this entity
                const entityToUpdate = mappingWorkConnectedWorksTags.data.find(x => x.id?.isEqualTo(event.entityID) === true);
                if (entityToUpdate != null) (entityToUpdate as any)[event.propertyName] = event.propertyValue;
            },
            deletedCallback: event => {
                const entityToDelete = mappingWorkConnectedWorksTags.data.find(x => x.id?.isEqualTo(event.entityID) === true);
                if (entityToDelete != null) mappingWorkConnectedWorksTags.data.splice(mappingWorkConnectedWorksTags.data.indexOf(entityToDelete), 1);
            },
            bulkMassiveChangeCallback: () => {
                MappingContainer.debounceMassiveChangeNotification();
            },
            massiveChangeCallback: () => {
                MappingContainer.loadMappingWorkConnectedWorksTag();
            }
        });
    }

    /**
    * Fetches the mapping work equal works tag for the given ID
    */
    public static loadMappingWorkEqualWorksTag(): void {
        const mappingWorkEqualWorksTags = new AsyncData<MappingWorkEqualWorksTag[]>([]);
        const api = useApi();
        usePayAuditStore().setEntityStoreData('_mappingWorkEqualWorksTag', { value: mappingWorkEqualWorksTags });

        mappingWorkEqualWorksTags.setData(api.mappingWorkEqualWorksTag.getBySelection({
            Condition: {
                LogicalOperator: LogicalOperator.And,
                Conditions: [
                    {
                        ConditionOperator: ConditionOperator.EqualTo,
                        PropertyName: 'MappingID_ID',
                        Value: MappingContainer.mappingId
                    },
                    {
                        ConditionOperator: ConditionOperator.NotEqualTo,
                        PropertyName: 'TagID_ID',
                        Value: null
                    },
                ]
            },
            QueriedFields: [
                'MappingWorkID',
                'TagID',
                'TagState'
            ]
        }));

        // custom subscriptions because adding values depends on the existance of a corresponding MappingWork
        MappingContainer.eventService.subscribe({
            componentId: MappingContainer.componentId,
            entity: 'MappingWorkEqualWorksTag',
            addedCallback: event => {
                const payAuditStore = usePayAuditStore();
                payAuditStore.addMappingWorkEqualWorksTag(event.entity as MappingWorkEqualWorksTag);
            },
            propertyUpdatedCallback: event => {
                // check we should be updating this entity
                const entityToUpdate = mappingWorkEqualWorksTags.data.find(x => x.id?.isEqualTo(event.entityID) === true);
                if (entityToUpdate != null) (entityToUpdate as any)[event.propertyName] = event.propertyValue;
            },
            deletedCallback: event => {
                const entityToDelete = mappingWorkEqualWorksTags.data.find(x => x.id?.isEqualTo(event.entityID) === true);
                if (entityToDelete != null) mappingWorkEqualWorksTags.data.splice(mappingWorkEqualWorksTags.data.indexOf(entityToDelete), 1);
            },
            bulkMassiveChangeCallback: () => {
                MappingContainer.debounceMassiveChangeNotification();
            },
            massiveChangeCallback: () => {
                MappingContainer.loadMappingWorkEqualWorksTag();
            }
        });
    }

    /**
    * Creates a subscription for the given entity
    * @param entityName name of the entity
    * @param entityData entity data to update
    * @param verificationProperty property to use when verifying changes (used when entity has no direct FK to mapping)
    * @param verificationID the id used to verify the store change (used when entity has no direct FK to mapping),
    * @param massiveChangeFunction function to run when a maasive change event is recieved
    */
    public static addSubscriptions(
        entityName: string,
        entityData: AsyncData<any>,
        verificationProperty: string,
        verificationID: Guid,
        massiveChangeFunction?: Function,
    ): void {
        // create the subscription
        MappingContainer.eventService.subscribe({
            componentId: MappingContainer.componentId,
            entity: entityName,
            addedCallback: event => {
                // check that the entity is not already in the store && check if data should be added to the store bassed on a verification property (i.e only entities taht belong to this mapping should be added)
                if (Array.isArray(entityData.data)) {
                    if (entityData.data.some(x => x.id.isEqualTo(event.entity.id) === true) || (event.entity as any)[verificationProperty]?.id?.isEqualTo(verificationID) !== true) return;
                } else if (entityData.data.id.isEqualTo(event.entity.id) === true || (event.entity as any)[verificationProperty]?.id?.isEqualTo(verificationID) !== true) return;

                // skip adding a new entity if the store doesnt accept arrays
                if (Array.isArray(entityData.data)) {
                    entityData.data.push(event.entity);
                }
            },
            propertyUpdatedCallback: event => {
                // check we should be updating this entity
                if (Array.isArray(entityData.data)) {
                    const entityToUpdate = entityData.data.find(x => x.id?.isEqualTo(event.entityID) === true);
                    if (entityToUpdate != null) (entityToUpdate as any)[event.propertyName] = event.propertyValue;
                } else if (entityData.data.id.isEqualTo(event.entityID) === true) {
                    entityData.data[event.propertyName] = event.propertyValue;
                }
            },
            deletedCallback: event => {
                if (Array.isArray(entityData.data)) {
                    const entityToDelete = entityData.data.find(x => x.id?.isEqualTo(event.entityID) === true);
                    if (entityToDelete != null) entityData.data.splice(entityData.data.indexOf(entityToDelete), 1);
                } else if (entityData.data.id.isEqualTo(event.entityID) === true) {
                    entityData.data.clear();
                }
            },
            bulkMassiveChangeCallback: () => {
                MappingContainer.debounceMassiveChangeNotification();
            },
            massiveChangeCallback: () => {
                if (massiveChangeFunction != null) massiveChangeFunction();
            },
        });
    }

    /**
    * Creates a subscription for the given entity
    * @param entityName name of the entity
    * @param entityData entity data to update
    * @param entityIndex the index to use when updating the data
    * @param verificationProperty property to use when verifying changes (used when entity has no direct FK to mapping)
    * @param verificationID the id used to verify the store change (used when entity has no direct FK to mapping),
    * @param massiveChangeFunction function to run when a maasive change event is recieved
    */
    public static addSubscriptionsWithIndex(
        entityName: string,
        entityData: AsyncData<any[]>,
        entityIndex: AsyncData<Map<string, number>>,
        verificationProperty: string,
        verificationID: Guid,
        massiveChangeFunction?: Function,
        copiedFields?: Map<string, string>,
    ): void {
        // create the subscription
        MappingContainer.eventService.subscribe({
            componentId: MappingContainer.componentId,
            entity: entityName,
            addedCallback: event => {
                // check we should be updating this entity
                if ((event.entity as any)[verificationProperty]?.id?.isEqualTo(verificationID) !== true) return;
                const entity = event.entity as any;
                // check the entity isnt already in the store (this can happen on first import)
                if (entityData.data.some(x => x.id.isEqualTo(entity.id) === true)) return;
                entityData.data.push(entity);
                entityIndex.data.set(entity.id!.toString(), entityData.data.indexOf(entity));
            },
            propertyUpdatedCallback: event => {
                const index = entityIndex.data.get(event.entityID.toString());
                if (index != null) {
                    (entityData.data[index!] as any)[event.propertyName] = event.propertyValue;
                    // Update the copied field as well if there is one
                    if (copiedFields?.has(event.propertyName) === true) {
                        const copiedFieldValue = copiedFields.get(event.propertyName);
                        if (copiedFieldValue != null) {
                            (entityData.data[index!] as any)[copiedFieldValue] = (entityData.data[index!] as any)[copiedFieldValue] ?? {};
                            (entityData.data[index!] as any)[copiedFieldValue].name = event.propertyValue;
                        }
                    }
                }
            },
            deletedCallback: event => {
                const index = entityIndex.data.get(event.entityID.toString());
                if (index != null) {
                    entityData.data.splice(index, 1);
                    entityIndex = MappingContainer.createIndex(entityData);
                }
            },
            bulkMassiveChangeCallback: () => {
                MappingContainer.debounceMassiveChangeNotification();
            },
            massiveChangeCallback: () => {
                if (massiveChangeFunction != null) massiveChangeFunction();
            },
        });
    }

    /**
    * Adds a notification for massive event changes to notify users that data has been updated.
    */
    public static massiveChangeNotification(): void {
        useNotificationStore().addByParams(
            Guid.create(),
            i18n.global.t('components.mappingContainer.massiveChangeNotification'),
            NotificationStates.Warning
        );
    }

    /**
    * Adds a notification for massive event changes to notify users that data has been updated.
    */
    public static changeWorkNotification(message: string, userIsCurrentUser: boolean): void {
        useNotificationStore().addByParams(
            Guid.create(),
            message,
            userIsCurrentUser
                ? NotificationStates.Success
                : NotificationStates.Warning
        );
    }

    /**
    * Adds the newly created item to the store after confirmation of creation in the database
    * @param entityType the entity type to add
    * @param value the entity/entities to add
    */
    public static addToStore(entityType: keyof Api, value: any | any[]): Promise<any>[] {
        const addValues = Array.isArray(value) ? value : [value];
        const promises: Promise<any>[] = [];
        const manager = getApiManager(entityType);
        const payAuditStore = usePayAuditStore();
        addValues.forEach((addValue: any) => {
            if ('create' in manager) {
                const promise = manager.create(addValue).then((addedEntity: any) => {
                    const newPayload: IEntityDataStatePayload = { value: addedEntity, promise };
                    payAuditStore.addEntityStoreData(`_${entityType}`, newPayload);
                }).catch((err: any) => {
                    throw err;
                });
                promises.push(promise);
            }
        });

        return promises;
    }

    /**
    * Deletes the an item from the store after confirmation of deletion from the database
    * @param entityType the entity type to delete
    * @param id the entity id(s) to delete
    * @param inStoreOnly update the value only in the store, not in the db
    */
    public static deleteFromStore(entityType: keyof Api, id: Guid | Guid[], inStoreOnly?: boolean): void {
        const deleteIds = Array.isArray(id) ? id : [id];
        const payAuditStore = usePayAuditStore();
        const manager = getApiManager(entityType);

        deleteIds.forEach((deleteId: any) => {
            if ('delete' in manager) {
                const promise = inStoreOnly === true
                    ? Promise.resolve()
                    : manager.delete([deleteId]);
                const newPayload: IEntityDataStatePayload = { id: deleteId, promise };

                payAuditStore.deleteEntityStoreData(`_${entityType}`, newPayload);
                if (entityType === 'mappingEmployment') {
                    payAuditStore.deleteMappingEmploymentIndexStoreData(deleteId.toString());
                }
            }
        });
    }

    /**
    * Attemps to update an item in the store
    * @param entityType the entity type to update
    * @param id the id of the entity to update
    * @param field the field name to update
    * @param value the value to update
    * @param inStoreOnly update the value only in the store, not in the db
    */
    public static async updateStore(entityType: keyof Api, id: Guid, field: string, value: unknown, inStoreOnly?: boolean, databaseValue?: unknown): Promise<any> {
        const manager = getApiManager(entityType) as any;

        const promise = inStoreOnly === true
            ? Promise.resolve()
            : manager.update(id, field, databaseValue ?? value, null, true);

        const payload: IEntityDataStatePayload = { id: id.toString(), field, value, promise };

        const payAuditStore = usePayAuditStore();

        payAuditStore.updateEntityStoreData(`_${entityType}`, payload);
        return promise;
    }
}

export enum CreateWorkOptions {
    ChosenEmploymentsPosition = 'position',
    ChosenEmploymentsStatisticalCode = 'statisticalCodeID',
    ChosenEmploymentsWork = 'workID',
    PreviousMapping = 'previousMapping',
    None = 'none'
}

export enum TabContainers {
    Analyze = 'analyze',
    ActionPlan = 'actionPlan',
    Details = 'details'
}
